import { Text } from 'app/design/primitives/text';
import { type useSharingPlaceMembers } from 'app/network/sharing-place';
import { useTranslation } from 'react-i18next';
import { AmbassadorBottomSheet } from '../../../profile/ambassador/ambassador-bottom-sheet';
import { DiscountDetailBottomSheet } from '../bottom-sheets/discount-details';
import { JoinPlaceBottomSheet } from '../bottom-sheets/join-place';
import { LeavePlaceBottomSheet } from '../bottom-sheets/leave-place';
import { OpeningHoursBottomSheet } from '../bottom-sheets/opening-hours';
import { PlaceMembersBottomSheet } from '../bottom-sheets/place-members';
import { SharingPlaceHelpBottomSheet } from '../bottom-sheets/sharing-place-help';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconAlertTriangle } from '@tabler/icons-react-native';
import { theme } from './shared';
import type { PlaceDetails, ProfileNamespace, ProfileNavigation } from './shared';

export function ProfileBottomSheets({
  ambassador,
  place,
  report,
}: {
  ambassador: React.ComponentProps<typeof AmbassadorBottomSheet>;
  place?: {
    details: PlaceDetails;
    discount: Pick<React.ComponentProps<typeof DiscountDetailBottomSheet>, 'onClose' | 'open'>;
    help: Pick<React.ComponentProps<typeof SharingPlaceHelpBottomSheet>, 'onClose' | 'open'>;
    join: Omit<React.ComponentProps<typeof JoinPlaceBottomSheet>, 'onClose'> & { onClose: () => void };
    leave: Omit<React.ComponentProps<typeof LeavePlaceBottomSheet>, 'onClose'> & { onClose: () => void };
    members: NonNullable<ReturnType<typeof useSharingPlaceMembers>['data']>;
    membersSheet: Pick<React.ComponentProps<typeof PlaceMembersBottomSheet>, 'onClose' | 'open'>;
    navigation: ProfileNavigation;
    openingHours: Pick<React.ComponentProps<typeof OpeningHoursBottomSheet>, 'onClose' | 'open'>;
  };
  report: {
    namespace: ProfileNamespace;
    onClose: () => void;
    onConfirm: () => void;
    open: boolean;
  };
}) {
  const { t } = useTranslation();
  return (
    <>
      <AmbassadorBottomSheet {...ambassador} />
      <BottomSheet onClose={report.onClose} open={report.open}>
        <BottomSheet.Action
          onPress={report.onConfirm}
          prefix={<IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} />}
        >
          <Text color="surface.error.default">{t('reportUser', { ns: report.namespace })}</Text>
        </BottomSheet.Action>
      </BottomSheet>
      {place ? (
        <>
          <OpeningHoursBottomSheet {...place.openingHours} placeDetails={place.details} />
          <JoinPlaceBottomSheet {...place.join} />
          <SharingPlaceHelpBottomSheet {...place.help} placeDetails={place.details} />
          <LeavePlaceBottomSheet {...place.leave} />
          <DiscountDetailBottomSheet {...place.discount} discount={place.details.discount} />
          <PlaceMembersBottomSheet {...place.membersSheet} members={place.members} navigation={place.navigation} />
        </>
      ) : null}
    </>
  );
}
